import React from 'react'

import styled from '@emotion/styled/macro'

import { getRelativeSizePt, paragraphFontSize } from './basis'

type ParagraphProps<T> = {
  id?: T
  onToggle?: (id: T | undefined) => void
  selectedId?: string
  isSelected?: boolean
  children: React.ReactNode
  isEditable: boolean | undefined
  underlined?: boolean
  onChange?: (e, id: T) => void
  fontSize?: string
  warningMark?: boolean
}

const backgroundColors = {
  required: 'rgb(233, 213, 2, 0.21)',
  isEditable: 'rgba(146, 241, 227, 0.21)',
  default: 'rgb(255,255,255)',
}

const underlineColors = {
  underlined: '#000',
  standard: '#fff',
}

const getUnderlineColor = ({
  underlined,
  relative,
}: ParagraphProps<unknown> & { relative?: boolean }) => {
  let underlineColor: string

  if (underlined) {
    underlineColor = underlineColors.underlined
  } else {
    underlineColor = underlineColors.standard
  }

  return `${underlineColor} ${relative ? getRelativeSizePt(1) : ''}`
}

const getBackgroundColor = ({
  isEditable,
  warningMark,
}: ParagraphProps<unknown>) => {
  if (warningMark && isEditable) {
    return backgroundColors.required
  }
  if (!warningMark && isEditable) {
    return backgroundColors.isEditable
  }
  return backgroundColors.default
}

const StyledParagraph = styled.p<ParagraphProps<any>>`
  font-family: 'PT-Astra-Serif', serif;
  font-style: italic;
  font-weight: normal;
  text-decoration: none;
  font-size: ${props => props.fontSize};
  line-height: ${getRelativeSizePt(21)};
  min-height: ${getRelativeSizePt(21)};

  min-width: ${getRelativeSizePt(12)};
  padding: 0 ${getRelativeSizePt(2)};
  margin: 0;

  color: black;

  background: linear-gradient(
    to bottom,
    ${props => getBackgroundColor(props)},
    ${props => getBackgroundColor(props)},
    ${getRelativeSizePt(20)},
    ${props => getUnderlineColor({ ...props, relative: props.underlined })},
    ${props => getUnderlineColor(props)}
  );

  background-size: 100% ${getRelativeSizePt(21)};

  background-position: 0 -0.4pt;

  cursor: pointer;
  outline: ${props => (props.isSelected ? '3px solid #0088FF7F' : 'none')};

  &:hover {
    outline: 3pt solid rgba(113, 201, 217, 0.5);
  }

  &:active {
    outline: 3pt solid rgba(6, 102, 191, 0.5);
  }
`

export function Paragraph<T extends string | undefined>({
  onToggle,
  selectedId,
  isSelected,
  isEditable,
  underlined = true,
  fontSize = paragraphFontSize,
  onChange,
  ...rest
}: ParagraphProps<T>) {
  return (
    <StyledParagraph
      isSelected={selectedId === rest.id ?? isSelected}
      onClick={onToggle ? () => onToggle(rest.id) : undefined}
      {...rest}
      fontSize={fontSize}
      isEditable={isEditable}
      underlined={underlined}
    />
  )
}
